import { useEffect, useRef } from 'react';
import { MOMENT_BADGES, moveNumberLabel, type Moment } from '../state/game-review';
import { ScrollArea } from './ui/scroll-area';

export interface GameMoveListProps {
  sans: readonly string[];
  startFen: string;
  moments: readonly Moment[];
  /** Plies played inside the repertoire (tinted as book). */
  inBook: number;
  /** The highlighted move's ply, or null at the start position. */
  activePly: number | null;
  onSelect: (ply: number) => void;
}

interface Cell {
  san: string;
  ply: number;
}

/** The builder's two-column move table, for a whole game with its moments marked. */
export function GameMoveList({ sans, startFen, moments, inBook, activePly, onSelect }: GameMoveListProps): JSX.Element {
  const ref = useRef<HTMLElement>(null);
  const rows: Array<{ number: string; white: Cell | null; black: Cell | null }> = [];
  sans.forEach((san, ply) => {
    const label = moveNumberLabel(ply, startFen);
    if (label.endsWith('.')) rows.push({ number: label, white: { san, ply }, black: null });
    else if (rows.length === 0) rows.push({ number: label.replace('…', '.'), white: null, black: { san, ply } });
    else rows[rows.length - 1].black = { san, ply };
  });

  useEffect(() => {
    const viewport = ref.current?.querySelector<HTMLElement>('[data-radix-scroll-area-viewport]');
    const current = viewport?.querySelector<HTMLElement>('[aria-current="step"]');
    if (!viewport || !current) return;
    const panel = viewport.getBoundingClientRect();
    const move = current.getBoundingClientRect();
    const delta = move.top < panel.top ? move.top - panel.top : move.bottom > panel.bottom ? move.bottom - panel.bottom : 0;
    if (delta !== 0) viewport.scrollTo?.({ top: viewport.scrollTop + delta });
  }, [activePly]);

  return (
    <section ref={ref} className="panel move-history" aria-labelledby="game-moves-title">
      <h2 id="game-moves-title">Moves</h2>
      <ScrollArea className="h-56" aria-label="Game notation">
        <table>
          <thead>
            <tr>
              <th scope="col"><span className="sr-only">Move number</span></th>
              <th scope="col">White</th>
              <th scope="col">Black</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.number}>
                <td className="move-no muted">{row.number}</td>
                {[row.white, row.black].map((cell, column) => {
                  if (!cell) return <td key={column} />;
                  const moment = moments.find((m) => m.ply === cell.ply);
                  const status = moment ? `, ${MOMENT_BADGES[moment.kind]}` : cell.ply < inBook ? ', in preparation' : '';
                  return (
                    <td key={column}>
                      <button
                        type="button"
                        className={[
                          cell.ply === activePly ? 'current' : '',
                          cell.ply < inBook ? 'in-book' : '',
                          moment ? `flagged flagged-${moment.kind}` : '',
                        ].filter(Boolean).join(' ')}
                        aria-current={cell.ply === activePly ? 'step' : undefined}
                        aria-label={`${moveNumberLabel(cell.ply, startFen)}${cell.san}${status}`}
                        onClick={() => onSelect(cell.ply)}
                      >
                        {cell.san}
                        {moment && <span className="moment-dot" aria-hidden="true" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </section>
  );
}
